/**
 * MetaUtmTemplate — modelo de UTM dos anúncios da Meta, por cliente (issue #318).
 *
 * A Meta não cria UTMs sozinha: ela só troca as macros ({{campaign.name}}...) pelo valor real
 * no clique, e só se o parâmetro estiver escrito no anúncio. Aqui o owner/admin define o modelo
 * que pré-preenche a etapa Anúncio da nova campanha (vale para anúncios criados depois).
 * Macros e caracteres aceitos são validados no backend; aqui validamos igual para avisar antes.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import Layout from '../../components/Layout.jsx'
import { useApp } from '../../context/AppContext.jsx'
import { getMetaUtmTemplate, saveMetaUtmTemplate } from '../../db/api.js'
import { useAccess } from '../../hooks/useAccess.js'
import { isDemo } from '../../lib/metaCampaignDemo.js'
import {
  DEFAULT_MACROS, UTM_FIELDS, emptyTemplate, exampleLink, insertAt, templateToForm, validateTemplate,
} from '../../lib/metaUtm.js'

// Sem período: é um modelo de configuração, não um relatório por data.
const BREADCRUMB = [{ label: 'Marketing' }, { label: 'Meta Ads', to: '/meta-ads' }, { label: 'UTMs' }]

// Modo demonstração (somente desenvolvimento, `?demo=1`): guarda em memória, sem backend.
let demoSaved = { source: 'facebook', medium: 'paid_social', campaign: '{{campaign.name}}', content: '{{ad.name}}', term: '' }
const demoApi = {
  load: async () => ({ template: demoSaved, macros: DEFAULT_MACROS }),
  save: async ({ utm }) => { demoSaved = { ...utm }; return { template: demoSaved } },
}
const realApi = {
  load: getMetaUtmTemplate,
  save: saveMetaUtmTemplate,
}

export default function MetaUtmTemplate() {
  const { activeClient } = useApp()
  const { isOrgAdmin } = useAccess()
  const demo = isDemo(useLocation().search)
  const api = demo ? demoApi : realApi
  const clientId = activeClient?.id ?? null

  const [form, setForm] = useState(emptyTemplate())
  const [saved, setSaved] = useState(emptyTemplate())
  const [macros, setMacros] = useState(DEFAULT_MACROS)
  const [status, setStatus] = useState('loading')
  const [notice, setNotice] = useState(null)
  const [busy, setBusy] = useState(false)
  const [sampleLink, setSampleLink] = useState('https://seusite.com.br/pagina')
  const [active, setActive] = useState('campaign')
  const inputs = useRef({})

  useEffect(() => {
    if (!clientId || !isOrgAdmin) return undefined
    let alive = true
    setStatus('loading')
    api.load(clientId)
      .then((res) => {
        if (!alive) return
        const next = templateToForm(res.template)
        setForm(next)
        setSaved(next)
        if (res.macros?.length) setMacros(res.macros)
        setStatus('ready')
      })
      .catch((err) => { if (alive) { setNotice({ tone: 'error', text: err.message || 'Não foi possível carregar o modelo.' }); setStatus('error') } })
    return () => { alive = false }
  }, [clientId, isOrgAdmin, api])

  const errors = useMemo(() => validateTemplate(form, macros), [form, macros])
  const dirty = UTM_FIELDS.some(({ key }) => form[key].trim() !== saved[key])
  const invalid = Object.keys(errors).length > 0

  function insertMacro(token) {
    const el = inputs.current[active]
    const { text, caret } = insertAt(form[active], token, el?.selectionStart, el?.selectionEnd)
    setForm((f) => ({ ...f, [active]: text }))
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange?.(caret, caret) })
  }

  async function save() {
    if (invalid) return
    setBusy(true)
    setNotice(null)
    try {
      const clean = Object.fromEntries(UTM_FIELDS.map(({ key }) => [key, form[key].trim()]))
      await api.save({ clientId, utm: clean })
      setForm(clean)
      setSaved(clean)
      setNotice({ tone: 'ok', text: 'Modelo salvo. Os próximos anúncios já vêm com esses valores.' })
    } catch (err) {
      setNotice({ tone: 'error', text: err.message || 'Não foi possível salvar.' })
    } finally {
      setBusy(false)
    }
  }

  let body
  if (!isOrgAdmin) {
    body = <div className="mcn-banner is-warn">Apenas owner e admin da organização podem configurar as UTMs dos anúncios.</div>
  } else if (!clientId) {
    body = <div className="mcn-banner">Selecione um cliente no topo da tela para configurar as UTMs.</div>
  } else if (status === 'loading') {
    body = <div className="mcn-banner">Carregando…</div>
  } else if (status === 'error') {
    body = <div className="mcn-banner is-error" role="alert">{notice?.text}</div>
  } else {
    body = (
      <>
        {demo && <div className="mcn-banner is-warn">Modo demonstração (somente desenvolvimento): nada é salvo no servidor.</div>}
        <div className="mcn-banner">
          A Meta não cria UTMs sozinha: ela só troca as macros, como <code>{'{{campaign.name}}'}</code>, pelo valor real quando alguém clica,
          e só se o parâmetro estiver escrito no anúncio. Este modelo vale para os anúncios criados depois de salvar e pode ser
          ajustado em cada anúncio.
        </div>
        {notice && <div className={`mcn-banner is-${notice.tone === 'ok' ? 'ok' : 'error'}`} role="status">{notice.text}</div>}

        <section className="mcn-card">
          <div className="mcn-card-title">Modelo padrão</div>
          <div className="mcn-card-sub">Deixe em branco o parâmetro que não deve ir no link.</div>
          <div className="mcn-grid-2">
            {UTM_FIELDS.map(({ key, param, label, help }) => (
              <div className="field" key={key}>
                <label className="field-label" htmlFor={`utm-${key}`}>{label} ({param})</label>
                <input
                  id={`utm-${key}`} className="input" maxLength={100} value={form[key]}
                  ref={(el) => { inputs.current[key] = el }}
                  onFocus={() => setActive(key)}
                  onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
                />
                {errors[key] ? <div className="mcn-error" role="alert">{errors[key]}</div> : <div className="field-hint">{help}</div>}
              </div>
            ))}
          </div>
        </section>

        <section className="mcn-card">
          <div className="mcn-card-title">Macros da Meta</div>
          <div className="mcn-card-sub">Clique para inserir no campo selecionado ({UTM_FIELDS.find((f) => f.key === active)?.param}).</div>
          <div className="mcn-macros">
            {macros.map((m) => (
              <button type="button" key={m.token} className="mcn-macro" onClick={() => insertMacro(m.token)}>
                <code>{m.token}</code>
                <small>{m.label}</small>
              </button>
            ))}
          </div>
        </section>

        <section className="mcn-card">
          <div className="mcn-card-title">Link final de exemplo</div>
          <div className="mcn-card-sub">Como o endereço fica com valores fictícios no lugar das macros.</div>
          <div className="field">
            <label className="field-label" htmlFor="utm-sample">Link de destino de exemplo</label>
            <input id="utm-sample" className="input" value={sampleLink} onChange={(e) => setSampleLink(e.target.value)} />
          </div>
          <div className="mcn-example"><code>{exampleLink(sampleLink, form)}</code></div>
        </section>

        <div className="mcn-footer">
          <span className="field-hint">{invalid ? 'Corrija os campos destacados para salvar.' : dirty ? 'Há alterações não salvas.' : 'Tudo salvo.'}</span>
          <button type="button" className="btn btn-black" disabled={busy || !dirty || invalid} onClick={save}>
            {busy ? 'Salvando…' : 'Salvar modelo'}
          </button>
        </div>
      </>
    )
  }

  return (
    <Layout title="Meta Ads — UTMs" breadcrumb={BREADCRUMB}>
      <div className="mcn">{body}</div>
    </Layout>
  )
}
